import {
  AttrField,
  BelongsToField,
  BooleanField,
  HasManyByField,
  HasManyField,
  OrmModel,
  StringField,
} from 'vuex-orm-decorators'
import { modelNames } from '@/data/models/types'
import BaseModel from '@/data/models/BaseModel'
import Country from '@/data/models/domain/Country'
import User from '@/data/models/domain/User'
import ContactList from '@/data/models/domain/ContactList'
import Note from '@/data/models/domain/Note'
import type { TmCountryCode } from '@/services/types'
import { CustomFieldValue } from '@/data/models/domain/CustomFieldValue'
import { CUSTOM_FIELD_FORM_FIELD_NAME } from '@/services/domain/cusomFields/types'
import type { UnsubscribeReason } from '@/data/models/domain/UnsubscribedContact'
import type { UnsubscribeEmailReasonType } from '@/data/models/domain/UnsubscribedEmailContact'
import Tag from '@/data/models/domain/Tag'

export type AvatarFile = {
  id?: string
  href: string
}

type ContactSubscriptionInfoItem<T> = null | {
  reason: T
}

export interface ContactEmailSubscription {
  email: ContactSubscriptionInfoItem<UnsubscribeEmailReasonType>
  phone: ContactSubscriptionInfoItem<UnsubscribeReason>
}

export type ContactSourceForm = {
  id: number
  name: string | null
  isDeleted: boolean
}

export type ContactSmsConsent = {
  marketing: boolean
  transactional: boolean
  termsAccepted: boolean
}

export const getContactFullName = (contact: Pick<Contact, 'firstName' | 'lastName'>) => {
  return [contact.firstName, contact.lastName]
    .filter((t) => t !== null)
    .join(' ')
    .trim()
}

@OrmModel(modelNames.CONTACT)
export default class Contact<TPhone extends string | null = string | null> extends BaseModel {
  @StringField()
  public id: string

  @AttrField(null)
  public firstName: string | null

  @AttrField(null)
  public lastName: string | null

  @AttrField(null)
  public countryId: TmCountryCode | null

  @BelongsToField(Country, 'countryId')
  public country: Country | null

  @StringField(null)
  public phone: TPhone

  @AttrField('')
  public whatsappPhone: string

  @StringField(null)
  public email: string | null

  @StringField(null)
  public companyName: string | null

  @AttrField()
  public avatar: AvatarFile | null

  @BooleanField()
  public blocked: boolean

  @BooleanField()
  public unsubscribed: boolean

  @StringField(null)
  public archivedAt: string | null

  @StringField()
  public userId: string

  @BelongsToField(User, 'userId')
  public user: User

  @AttrField([], (value: number[]) => value?.map((item) => item.toString()))
  public listIds: string[]

  @HasManyByField(ContactList, 'listIds')
  public lists: ContactList[]

  @HasManyField(CustomFieldValue, 'entityId')
  public ['customFieldsValues']: CustomFieldValue[]

  @AttrField([], (value: number[]) => value?.map((item) => item.toString()))
  public noteIds: string[]

  @HasManyByField(Note, 'noteIds')
  public notes: Note[]

  @AttrField(null, (value) => (value ? `${value}` : null))
  public ownerId: string | null

  @BelongsToField(User, 'ownerId')
  public owner: User | null

  @AttrField([], (value: number[]) => value?.map((item) => item.toString()))
  public tagIds: string[]

  @HasManyByField(Tag, 'tagIds')
  public tags: Tag[]

  @StringField(null)
  public createdAt: string | null

  @AttrField(null, (value) => (value ? `${value}` : null))
  public creatorId: string | null

  @BelongsToField(User, 'creatorId')
  public createdBy: User | null

  @StringField(null)
  public updatedAt: string | null

  @AttrField(null, (value) => (value ? `${value}` : null))
  public updaterId: string | null

  @BelongsToField(User, 'updaterId')
  public updatedBy: User | null

  @StringField(null)
  public fbMessengerSenderId: string | null

  @StringField(null)
  public instagramSenderId: string | null

  @StringField(null)
  public source: ContactSource | null

  @AttrField()
  public emailSubscription: ContactEmailSubscription

  @AttrField(null)
  public sourceForm: ContactSourceForm | null

  @AttrField(null)
  public smsConsent: ContactSmsConsent | null

  public get fullName() {
    return getContactFullName(this)
  }

  public get isDeleted() {
    return !!this.archivedAt
  }

  public canSendMessage(): this is Contact<string> {
    return !!this.phone
  }

  public canCall(): this is Contact<string> {
    return !!this.phone
  }

  public get canBeBlocked() {
    return !this.unsubscribed && !this.blocked
  }

  public get canBeSubscribedByPhone() {
    return !!this.phone
  }

  public get isUnsubscribedByPhone() {
    return this.emailSubscription.phone !== null
  }
}
export enum ContactSource {
  MANUAL = 'manual',
  IMPORT = 'import',
  API = 'api',
  FORM = 'form',
  LIVE_CHAT = 'liveChat',
  WHATSAPP = 'whatsapp',
  FACEBOOK = 'facebook',
  TICKET = 'ticket',
  INSTAGRAM = 'instagram',
  SMS = 'sms',
  SHOPIFY = 'shopify',
  WOOCOMMERCE = 'woocommerce',
  HUBSPOT = 'hubspot',
  ZOHO = 'zohocrm',
  MCP = 'mcp',
}
